import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import {
  coerceWorkloadHours,
  parseWorkloadHours,
} from '@/features/projects/workload';

/**
 * Compact hours input for approximate task workload. Empty means
 * "not estimated". Commits on blur (and Enter) so typing doesn't
 * fire a mutation per keystroke — same pattern as the task name.
 */
export function WorkloadHoursInput({
  value,
  onCommit,
  disabled,
  className,
  id,
  placeholder = 'hrs',
  'aria-label': ariaLabel = 'Approximate workload in hours',
}: {
  value: number | null | undefined;
  onCommit: (next: number | null) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
}) {
  const canonical = coerceWorkloadHours(value);
  const [draft, setDraft] = useState(canonical != null ? String(canonical) : '');

  useEffect(() => {
    setDraft(canonical != null ? String(canonical) : '');
  }, [canonical]);

  const commit = () => {
    try {
      const next = parseWorkloadHours(draft);
      const prev = canonical;
      if (next === prev) {
        setDraft(next != null ? String(next) : '');
        return;
      }
      onCommit(next);
    } catch (err) {
      setDraft(canonical != null ? String(canonical) : '');
      toast.error(err instanceof Error ? err.message : 'Invalid hours.');
    }
  };

  return (
    <div className="relative">
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn('pr-6', className)}
      />
      <span
        className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground"
        aria-hidden="true"
      >
        h
      </span>
    </div>
  );
}

/**
 * Uncontrolled-style draft for create forms (new-task row, quick-add).
 * Parent owns the string so submit can parse once.
 */
export function WorkloadHoursDraftInput({
  value,
  onChange,
  disabled,
  className,
  placeholder = 'hrs',
  'aria-label': ariaLabel = 'Approximate workload in hours',
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
}) {
  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn('pr-6', className)}
      />
      <span
        className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground"
        aria-hidden="true"
      >
        h
      </span>
    </div>
  );
}
